'use client';

import React from 'react';
import { Button } from '@heroui/button';
import { Input } from '@heroui/input';
import Select, { GroupBase, StylesConfig } from "react-select";
import { TzSelectStylesFullWidth } from '@/shared/styleDefs';
type RemoteStorageOption = {
  value: string;
  label: string;
};
import { RemoteStorageType } from '@/shared/types';
import { REMOTE_STORAGE_OPTIONS } from './tenant-form-shared';
export interface TenantBannersAndRemoteStorageProps {
  banners: string[];
  onBannerChange: (index: number, value: string) => void;
  onAddBanner: () => void;
  onRemoveBanner: (index: number) => void;
  remoteStorageType: RemoteStorageType | undefined;
  remoteStorageUrl: string;
  onRemoteStorageTypeChange: (type: RemoteStorageType | undefined) => void;
  onRemoteStorageUrlChange: (url: string) => void;
  onClearError: () => void;
}
export function TenantBannersAndRemoteStorage({
  banners,
  onBannerChange,
  onAddBanner,
  onRemoveBanner,
  remoteStorageType,
  remoteStorageUrl,
  onRemoteStorageTypeChange,
  onRemoteStorageUrlChange,
  onClearError
}: TenantBannersAndRemoteStorageProps) {
  const handleTypeSelection = (newValue: unknown) => {
    const option = newValue as {
      value: string;
      label: string;
    } | null;
    const v = option?.value ?? '';
    const isNone = v === '';
    onRemoteStorageTypeChange(isNone ? undefined : v as RemoteStorageType);
    if (isNone) onRemoteStorageUrlChange('');
    onClearError();
  };
  return <div className="bg-slate-800/50 backdrop-blur-xl rounded-lg border border-purple-700/50 p-6 space-y-6" data-sentry-component="TenantBannersAndRemoteStorage" data-sentry-source-file="TenantBannersAndRemoteStorage.tsx">
      <h2 className="text-xl font-semibold text-purple-300 mb-4">Banners &amp; Remote Storage</h2>
      <div className="space-y-2">
        <span className="text-sm text-slate-300">Banners (optional)</span>
        <p className="text-xs text-slate-400">Add one or more banner names to show as tenant banners.</p>
        {banners.map((url, index) => <div key={index} className="flex gap-2 items-center">
            <Input value={url} onValueChange={v => onBannerChange(index, v)} placeholder="" labelPlacement="outside" classNames={{
          input: "box-border text-white",
          inputWrapper: "box-border"
        }} className="flex-1" />
            <Button type="button" size="sm" variant="light" onPress={() => onRemoveBanner(index)} className="text-slate-400 hover:text-red-400 shrink-0" aria-label="Remove banner">
              Remove
            </Button>
          </div>)}
        <Button type="button" size="sm" variant="flat" onPress={onAddBanner} className="text-purple-300 hover:text-white hover:bg-purple-500/20" data-sentry-element="Button" data-sentry-source-file="TenantBannersAndRemoteStorage.tsx">
          Add banner name
        </Button>
      </div>
      <label className="flex text-white/100 font-light tracking-wider text-[14px] m-0 mb-2 p-0">Remote Storage</label>
      <Select classNamePrefix="react-select" aria-label="Remote storage type (optional)" styles={{
      ...TzSelectStylesFullWidth,
      menu: (provided, state) => ({
        ...(TzSelectStylesFullWidth.menu?.(provided, state as Parameters<NonNullable<typeof TzSelectStylesFullWidth.menu>>[1]) ?? provided),
        zIndex: 9999,
        color: 'black'
      })
    } as StylesConfig<RemoteStorageOption, false, GroupBase<RemoteStorageOption>>} className="react-select-container font-inter pr-2 w-full custom-scrollbar2" value={remoteStorageType != null ? {
      value: remoteStorageType,
      label: REMOTE_STORAGE_OPTIONS.find(o => o.value === remoteStorageType)?.label ?? remoteStorageType
    } : {
      value: '',
      label: 'None'
    }} onChange={newValue => handleTypeSelection(newValue)} options={REMOTE_STORAGE_OPTIONS.map(option => ({
      value: option.value,
      label: option.label
    }))} noOptionsMessage={() => "No options available"} data-sentry-element="Select" data-sentry-source-file="TenantBannersAndRemoteStorage.tsx" />
      {remoteStorageType != null && <Input label="Remote storage URL *" name="remote_storage_url" value={remoteStorageUrl} onValueChange={v => {
      onRemoteStorageUrlChange(v);
      onClearError();
    }} labelPlacement="outside" placeholder="" classNames={{
      input: "box-border text-white",
      inputWrapper: "box-border"
    }} />}
    </div>;
}